import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Loader2, CalendarClock, PlayCircle, Tv } from "lucide-react";
import { useLiveStatus } from "../hooks/useLiveStatus";

const PAGE_URL = "https://ujjainmahakumbh.com/live";

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function useLiveSeo(featured) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Live Broadcast — Katha & Darshan | Ujjain Maha Kumbh 2028";
    const desc =
      "Watch live Katha, aarti and spiritual broadcasts for Ujjain Maha Kumbh (Simhastha) 2028, plus a library of past recordings.";
    let tag = document.querySelector('meta[name="description"]');
    const prevDesc = tag?.getAttribute("content");
    if (tag) tag.setAttribute("content", desc);
    let script;
    if (featured) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "live-video-jsonld";
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: featured.title,
        description: featured.description || desc,
        thumbnailUrl: `https://i.ytimg.com/vi/${featured.video_id}/hqdefault.jpg`,
        uploadDate: featured.created_at,
        embedUrl: `https://www.youtube.com/embed/${featured.video_id}`,
        url: PAGE_URL,
        ...(featured.is_live && {
          publication: { "@type": "BroadcastEvent", isLiveBroadcast: true, startDate: featured.created_at },
        }),
      });
      document.head.appendChild(script);
    }
    return () => {
      document.title = prevTitle;
      if (tag && prevDesc !== null) tag.setAttribute("content", prevDesc);
      script?.remove();
    };
  }, [featured]);
}

const Player = ({ item }) => (
  <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
    <iframe
      key={item.video_id}
      src={`https://www.youtube-nocookie.com/embed/${item.video_id}?autoplay=${item.is_live ? 1 : 0}&mute=${item.is_live ? 1 : 0}&rel=0`}
      title={item.title}
      className="absolute inset-0 w-full h-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      data-testid="live-player-iframe"
    />
  </div>
);

const OfflineCard = ({ title, at }) => (
  <div
    className="w-full aspect-video rounded-2xl border border-white/10 flex flex-col items-center justify-center text-center px-6"
    style={{ background: "rgba(44,44,46,0.55)", backdropFilter: "blur(18px)" }}
    data-testid="live-offline-card"
  >
    <Tv size={40} className="text-white/30 mb-4" />
    <span className="text-xs uppercase tracking-widest text-white/40 font-body mb-2">Offline</span>
    <h2 className="font-heading text-2xl sm:text-3xl text-white mb-2">Next broadcast coming soon</h2>
    {title && <p className="text-white/70 font-body" data-testid="next-broadcast-title">{title}</p>}
    {at && (
      <p className="mt-3 inline-flex items-center gap-2 text-saffron font-body text-sm" data-testid="next-broadcast-time">
        <CalendarClock size={16} /> {fmt(at)}
      </p>
    )}
  </div>
);

const VideoCard = ({ item, active, onSelect }) => {
  const upcoming = item.scheduled_at && new Date(item.scheduled_at) > new Date();
  return (
    <button
      onClick={() => onSelect(item)}
      className={`group text-left rounded-xl overflow-hidden border transition-colors duration-200 ${
        active ? "border-saffron/70" : "border-white/10 hover:border-white/25"
      }`}
      style={{ background: "rgba(44,44,46,0.5)" }}
      data-testid={`broadcast-card-${item.id}`}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={`https://i.ytimg.com/vi/${item.video_id}/hqdefault.jpg`}
          alt={item.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <PlayCircle className="absolute inset-0 m-auto text-white/80 opacity-0 group-hover:opacity-100 transition-opacity" size={40} />
        {upcoming && (
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-saffron text-stone-900 font-semibold">
            Upcoming
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="text-white text-sm font-semibold font-body line-clamp-2">{item.title}</p>
        {(item.scheduled_at || item.created_at) && (
          <p className="text-white/40 text-xs mt-1 font-body">{fmt(item.scheduled_at || item.created_at)}</p>
        )}
      </div>
    </button>
  );
};

export default function LiveBroadcast() {
  const { data, loading } = useLiveStatus();
  const [selected, setSelected] = useState(null);
  const featured = selected || data?.live || null;
  useLiveSeo(featured);

  if (loading) {
    return (
      <div className="min-h-screen pt-28 flex justify-center text-white/50" style={{ background: "#1c1917" }}>
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!data?.enabled) {
    return (
      <div className="min-h-screen pt-40 text-center px-4" style={{ background: "#1c1917" }} data-testid="live-page-disabled">
        <h1 className="font-heading text-3xl text-white mb-3">Live Broadcast is currently unavailable</h1>
        <Link to="/" className="text-saffron font-body text-sm hover:underline">Back to Home</Link>
      </div>
    );
  }

  const list = data.broadcasts || [];
  return (
    <div className="min-h-screen pt-28 pb-20" style={{ background: "#1c1917" }} data-testid="live-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 mb-5">
            <Radio size={14} className="text-red-400" />
            <span className="text-red-300 text-xs font-body uppercase tracking-widest">Live Broadcast</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-4">
            Katha &amp; Darshan, Live
          </h1>
          <p className="text-white/60 text-base font-body max-w-2xl">
            Join live Katha, aarti and spiritual discourses from Ujjain, or watch past recordings below.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          {featured ? <Player item={featured} /> : <OfflineCard title={data.next_broadcast_title} at={data.next_broadcast_at} />}
          {featured && (
            <div className="mt-5" data-testid="live-featured-info">
              <div className="flex items-center gap-3 mb-2">
                {featured.is_live && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold uppercase" data-testid="on-air-badge">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> On Air
                  </span>
                )}
                <h2 className="font-heading text-xl sm:text-2xl text-white" data-testid="live-featured-title">{featured.title}</h2>
              </div>
              {featured.description && <p className="text-white/60 font-body text-sm max-w-3xl">{featured.description}</p>}
              {selected && data.live && selected.id !== data.live.id && (
                <button onClick={() => setSelected(null)} className="mt-3 text-sm text-red-300 hover:text-red-200 font-body" data-testid="back-to-live-btn">
                  ← Back to live stream
                </button>
              )}
            </div>
          )}
        </motion.div>

        {list.length > 0 && (
          <section className="mt-14" data-testid="broadcast-playlist">
            <h2 className="font-heading text-lg text-white mb-5">More Broadcasts</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {list.map((b) => (
                <VideoCard key={b.id} item={b} active={featured?.id === b.id} onSelect={(it) => { setSelected(it); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
